import React, { useEffect, useRef } from 'react';
import { User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

const DolphinIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M21.32,3.27c-.55-.35-1.13-.52-1.71-.52s-1.16,.17-1.71,.52l-1.4,.89v-1.16c0-1.1-.9-2-2-2H6.5c-1.1,0-2,.9-2,2v3.78c-1.76,.89-3,2.71-3,4.82v4.8c0,2.76,2.24,5,5,5h2c1.1,0,2-.9,2-2v-1h2v1c0,1.1,.9,2,2,2h2c2.76,0,5-2.24,5-5v-4.8c0-1.23-.45-2.35-1.18-3.22l1.18-.75v1.97c0,.55,.45,1,1,1s1-.45,1-1v-4c0-.58-.17-1.16-.52-1.71-.35-.55-.83-.98-1.38-1.27zm-2.82,12.33c0,1.65-1.35,3-3,3h-2v-1c0-1.1-.9-2-2-2h-2c-1.1,0-2,.9-2,2v1h-2c-1.65,0-3-1.35-3-3v-4.8c0-1.65,1.35-3,3-3h8c1.65,0,3,1.35,3,3v4.8z"/>
    <circle cx="8.5" cy="11.5" r="1.5"/>
  </svg>
);

// Add conversion for plain text paths to markdown links.
const formatInternalLinks = (text) => {
  return text.replace(/(^|\s)(\/[\w/-]+)/g, (match, pre, path) => {
    const cleanPath = path.replace(/[.,?!]$/, '');
    const trailing = path.slice(cleanPath.length);
    const display = cleanPath.substring(1);
    return `${pre}[${display}](${cleanPath})${trailing}`;
  });
};

const ChatMessage = ({ message }) => {
  const messageRef = useRef(null);

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [message.text]);

  return (
    <div ref={messageRef} className={`flex items-start gap-2 ${message.isBot ? 'justify-start' : 'justify-end'}`}>
      {message.isBot && (
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
          <DolphinIcon />
        </div>
      )}
      <div className={`group relative max-w-xs md:max-w-sm p-3 rounded-lg ${
        message.isBot
          ? 'bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700'
          : 'bg-blue-600 text-white'
      }`}>
        <div className={`text-sm ${message.isBot ? 'text-gray-800 dark:text-gray-200' : 'text-white'}`}>
          <ReactMarkdown
            components={{
              a: ({ node, children, href, ...rest }) => (
                <Link to={href} {...rest} className="text-blue-500 hover:underline">
                  {children}
                </Link>
              ),
              p: ({ node, ...props }) => <p {...props} className="mb-2" />,
            }}
          >
            {formatInternalLinks(message.text)}
          </ReactMarkdown>
        </div>
      </div>
      {!message.isBot && (
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
};

const ChatMessages = ({ messages, loading, error }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
      {messages.map((message, index) => (
        <ChatMessage key={index} message={message} />
      ))}
      {loading && (
        <div className="flex justify-center">
          <div className="animate-pulse text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <DolphinIcon />
            <span>Thinking...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="text-red-500 text-center">{error}</div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
